/** Seller-declared used-part grades — platform does not physically inspect parts. */
export const GRADE_TO_CONDITION = { A: 'excellent', B: 'good', C: 'fair' };
export const CONDITION_TO_GRADE = { excellent: 'A', good: 'B', fair: 'C' };

export const LISTABLE_GRADES = ['A', 'B', 'C'];

export const GRADE_META = {
  A: {
    grade: 'A',
    label: 'Used — Like New',
    condition:
      'No visible damage, cracks, or corrosion; fully functional and tested; part number/label readable; removed from vehicle with <60,000 km.',
    photoRequirements:
      'Full part (all sides), label/part-number close-up, connection points/mating surfaces.',
    kmCap: '< 60,000 km',
    warrantyDays: 45,
    minPhotos: 3,
    commissionPct: '20–30%',
    expressBuyAllowed: true,
    selectable: true,
  },
  B: {
    grade: 'B',
    label: 'Used — Good Condition',
    condition:
      'Minor surface scratches or scuff marks; no structural damage or deep corrosion; fully functional; 60,000–120,000 km vehicle.',
    photoRequirements:
      'Full part, worst visible scratch/wear, functional surfaces/connections.',
    kmCap: '60,000–120,000 km',
    warrantyDays: 30,
    minPhotos: 3,
    commissionPct: '20–30%',
    expressBuyAllowed: false,
    selectable: true,
  },
  C: {
    grade: 'C',
    label: 'Used — Functional with Wear',
    condition:
      'Visible wear, minor dents, or surface corrosion; functional but shows age; no cracks through structural areas; >120,000 km vehicle.',
    photoRequirements:
      'Full part, ALL damage areas (mandatory), functional surfaces, part number/identification.',
    kmCap: '> 120,000 km',
    warrantyDays: 15,
    minPhotos: 4,
    commissionPct: '20–30%',
    expressBuyAllowed: false,
    selectable: true,
  },
  NOT_ELIGIBLE: {
    grade: 'NOT_ELIGIBLE',
    label: 'Not Eligible',
    condition:
      'Cracked housings, broken mounting points, corrosion through structural components, missing critical sub-components, or unverifiable function.',
    photoRequirements: null,
    kmCap: null,
    warrantyDays: 0,
    minPhotos: 0,
    commissionPct: null,
    expressBuyAllowed: false,
    selectable: false,
    tooltip: 'This part cannot be listed on Sparepe.',
  },
};

const GRADE_RANK = { A: 1, B: 2, C: 3 };

export function normalizeGrade(grade) {
  const g = String(grade || 'B').trim().toUpperCase();
  return LISTABLE_GRADES.includes(g) ? g : 'B';
}

export function getGradeMeta(grade) {
  const key = String(grade || '').trim().toUpperCase();
  if (key === 'NOT_ELIGIBLE') return GRADE_META.NOT_ELIGIBLE;
  return GRADE_META[normalizeGrade(grade)];
}

export function getMinPhotosForGrade(grade) {
  return getGradeMeta(grade).minPhotos || 0;
}

/** True when received grade is worse (higher rank) than listed grade. */
export function isGradeMisdeclaration(listedGrade, actualGrade) {
  const listed = normalizeGrade(listedGrade);
  const actual = normalizeGrade(actualGrade);
  return (GRADE_RANK[actual] || 3) > (GRADE_RANK[listed] || 1);
}

export function conditionToGrade(condition) {
  return CONDITION_TO_GRADE[String(condition || '').toLowerCase()] || '';
}

export function gradeToCondition(grade) {
  return GRADE_TO_CONDITION[normalizeGrade(grade)] || 'good';
}

/**
 * HARD RULE: Express Buy renders only for Grade A with complete photos.
 */
export function shouldShowExpressBuy(listing) {
  if (!listing) return false;
  const grade = normalizeGrade(listing.grade);
  if (grade !== 'A') return false;
  if (listing.photoVerified === false) return false;
  if (listing.expressBuyAvailable === false) return false;
  return getGradeMeta(grade).expressBuyAllowed;
}

export function shouldShowCheckAvailabilityPrimary(listing) {
  const grade = normalizeGrade(listing?.grade);
  return grade === 'B' || grade === 'C';
}

export function isCheckAvailabilityDisabled(listing) {
  return !listing?.photoVerified;
}

export function getCtaConfig(listing) {
  const grade = normalizeGrade(listing?.grade);
  const photosOk = !!listing?.photoVerified;
  const showExpress = shouldShowExpressBuy(listing);
  return {
    grade,
    photosOk,
    showExpressBuy: showExpress,
    showCheckAvailability: true,
    checkAvailabilityDisabled: !photosOk,
    checkAvailabilityPrimary: !showExpress,
    expressBuyPrimary: showExpress,
  };
}
